import { format } from 'date-fns';
import { isEmpty } from 'lodash';
import { transformFilesToBase64 } from '../../utils/transformFilesToBase64Format';
import {
  Accident,
  AccidentOperatorRequest,
  AccidentRequest,
  AccidentUserRequest,
  AccidentUserType,
  AccidentUserTypeValues
} from '../types/accident';

const formatDate = (date: Date | undefined): string => {
  return date ? format(date, 'yyyy-MM-dd HH:mm') : '';
};

const formatYear = (date: string | undefined): string => {
  return date ? format(date, 'yyyy') : '';
};

/**
 * Helper funkce pro odstranění undefined hodnot z objektu
 * Použití: removeUndefinedValues({ a: 1, b: undefined, c: 3 }) => { a: 1, c: 3 }
 * @param obj - Objekt, ze kterého se mají odstranit undefined hodnoty
 * @returns Nový objekt bez undefined hodnot
 */
const removeUndefinedValues = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined)) as Partial<T>;
};

const createUserData = (
  accident: Accident,
  isOperator?: boolean
): AccidentUserRequest | AccidentOperatorRequest | undefined => {
  const sourceUser = isOperator ? accident.operator : accident.user;

  // Pokud je operator a má equalTo="user", pošleme jen equalTo (kontrola PŘED isEmpty!)
  if (isOperator && (sourceUser as any)?.equalTo === 'user') {
    return { equalTo: 'user' } as AccidentOperatorRequest;
  }

  // Teprve pak kontrolujeme, zda je sourceUser prázdný
  if (!sourceUser || isEmpty(sourceUser) || Array.isArray(sourceUser)) {
    return undefined;
  }

  // Type může být buď string (z formuláře) nebo objekt s name (z API response)
  const typeName = typeof sourceUser.type === 'string' ? sourceUser.type : sourceUser.type?.name;
  const typeValue = typeName
    ? AccidentUserTypeValues[typeName as AccidentUserType]
    : AccidentUserTypeValues[AccidentUserType.Person]; // výchozí Person (1)

  // Explicitně vyjmenovat všechna pole, která chceme poslat do API (žádný spread!)
  const userObject: AccidentUserRequest = {
    type: typeValue,
    degree: sourceUser.degree,
    name: sourceUser.name,
    surname: sourceUser.surname,
    birthDate: sourceUser.birthDate,
    birthNumber: sourceUser.birthNumber,
    street: sourceUser.address?.street,
    houseNum: sourceUser.address?.houseNum,
    city: sourceUser.address?.city,
    zip: sourceUser.address?.zip,
    countryId: sourceUser.address?.countryId,
    companyName: sourceUser.company?.companyName,
    companyIdNum: sourceUser.company?.companyIdNum,
    companyTaxNum: sourceUser.company?.companyTaxNum,
    companyVatPayer: sourceUser.company?.companyVatPayer
  };

  const cleanedUserObject = removeUndefinedValues(userObject) as AccidentUserRequest;

  // Pokud je to operator, přidáme equalTo: "none" (standardní operátor)
  if (isOperator) {
    return {
      ...cleanedUserObject,
      equalTo: 'none'
    } as AccidentOperatorRequest;
  }

  return cleanedUserObject;
};

const createCarData = (car: Accident['car']) => {
  if (!car?.brand) return undefined;

  return {
    ...car,
    manufacturedYear: formatYear(car.manufacturedYear),
    stk: car.stk ? format(car.stk, 'yyyy-MM-dd') : undefined
  };
};

const createAccidentData = (accident: Accident) => {
  return {
    date: accident.date ? formatDate(accident.date as unknown as Date) : undefined,
    place: accident.place,
    note: accident.note,
    carDamageNote: accident.carDamageNote,
    internalNote: accident.internalNote,
    insuranceId: accident.insurance?.id,
    insuranceType: accident.insuranceType,
    insuranceCode: accident.insuranceCode,
    isPoliceHandled: accident.isPoliceHandled,
    hasParking: accident.hasParking,
    hasTowing: accident.hasTowing,
    hasPhotos: accident.hasPhotos,
    payoutInfo: accident.payoutInfo,
    payoutType: accident.payoutType,
    accidentReporting: accident.accidentReporting?.type,
    sourceCommissionValue: accident.source?.commissionValue,
    source: accident.source?.type ? accident.source : undefined
  };
};

const createDriverData = (accident: Accident) => {
  if (!accident.driver) return undefined;

  // Pokud je driver stejný jako user nebo operator, pošleme jen equalTo
  if (accident.driver.equalTo === 'user') {
    return { equalTo: 'user' };
  }
  if (accident.driver.equalTo === 'operator') {
    return { equalTo: 'operator' };
  }

  // Jinak pošleme plná data s equalTo: "none" (i když equalTo není definováno)
  return {
    ...accident.driver,
    equalTo: 'none'
  };
};

const createContactPersonData = (accident: Accident) => {
  if (!accident.contactPerson) return undefined;

  // Pokud je kontaktní osoba stejná jako user, operator nebo driver, pošleme jen equalTo
  if (accident.contactPerson.equalTo === 'user') {
    return { equalTo: 'user' };
  }
  if (accident.contactPerson.equalTo === 'operator') {
    return { equalTo: 'operator' };
  }
  if (accident.contactPerson.equalTo === 'driver') {
    return { equalTo: 'driver' };
  }

  const contactPersonObject = {
    degree: accident.contactPerson?.degree,
    name: accident.contactPerson?.name,
    surname: accident.contactPerson?.surname,
    phone: accident.contactPerson?.phone,
    birthNumber: accident.contactPerson?.birthNumber,
    equalTo: 'none' as const
  };

  return removeUndefinedValues(contactPersonObject);
};

const createNotificationData = (accident: Accident) => {
  if (!accident.notification?.email && !accident.notification?.phone) {
    return undefined;
  }

  return removeUndefinedValues({
    email: accident.notification?.email,
    phone: accident.notification?.phone
  });
};

export const parseAccidentDataToRequest = (accident: Accident, sendAccidentData = true): AccidentRequest => {
  const optionalData = {
    accident: sendAccidentData ? createAccidentData(accident) : undefined,
    car: createCarData(accident.car),
    user: createUserData(accident),
    operator: createUserData(accident, true),
    contactPerson: createContactPersonData(accident),
    driver: createDriverData(accident),
    notification: createNotificationData(accident)
  };

  return removeUndefinedValues(optionalData) as AccidentRequest;
};

export const parseFilesToUploadRequest = async (files: File[], template: string) => {
  const transformedFiles = await transformFilesToBase64(files);

  const base64Files = transformedFiles.map((file) => ({
    filename: file.name,
    data: file.data as string,
    template: template
  }));

  return { files: base64Files };
};

export const parseAccidentPhotoNoteToAccident = (value: string): string => {
  const parts = value.split('-');
  const photoKey = parts[1] ?? '';
  return `${photoKey}Note`;
};
